import React, { ReactElement } from "react";
import { FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";
import { formatBytes } from "helpers/numberFormatting";

import Divider from "@mui/material/Divider";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import { useTheme } from "@mui/material/styles";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Unstable_Grid2";
import useMediaQuery from "@mui/material/useMediaQuery";

import { locationList } from "constants/locations";

import SemiCircularProgress from "components/shared/progressBar/SemiCircularProgress";

import { getSelectedCloudboxData } from "redux/selectors/cloudboxSelector";

const CloudboxMonitoring = (): ReactElement => {
 const theme = useTheme();
 const desktopViewPort = useMediaQuery(theme.breakpoints.up("lg"));
 const cloudboxData = useSelector(getSelectedCloudboxData);

 const RenderLocation = (location: string) => {
  const filteredLocation = locationList.filter((data) => location === data.tag);
  return filteredLocation.map((element, index) => (
   <Stack key={`location-item-${index}`} direction="row" spacing={1}>
    <Stack component="span" className={`fi fi-${element.flag}`} />
    <Typography variant="inherit">
     <FormattedMessage id={element.location} />
    </Typography>
   </Stack>
  ));
 };

 return cloudboxData ? (
  <Paper elevation={1} sx={{ borderRadius: "5px", p: 2 }}>
   <Stack justifyContent="space-between" direction={desktopViewPort ? "row" : "column"}>
    <Stack>
     <Stack>
      <Typography
       fontWeight="bold"
       fontSize={16}
       textAlign="left"
       variant="unboundedTitle"
       textTransform="uppercase">
       {cloudboxData?.name || "N/A"}
      </Typography>
      {cloudboxData?.location && RenderLocation(cloudboxData.location.country.toLowerCase())}
     </Stack>
    </Stack>
   </Stack>
   <Divider sx={{ my: 1 }} />
   <Grid container>
    <Grid xs={4}>
     <Stack alignItems="center">
      <SemiCircularProgress
       value={Number(
        ((cloudboxData?.stats.size_data / cloudboxData?.storage_box_type.size) * 100).toFixed(0)
       )}
       maxValue={100}
       size={80}
       thickness={8}
       percent={true}
      />
      <Typography variant="extraSmall" mt={0.5} textTransform="uppercase" fontWeight="bold">
       <FormattedMessage id="server.disk" />:
       {formatBytes(cloudboxData.storage_box_type.size, "TB") < 1
        ? formatBytes(cloudboxData.storage_box_type.size, "GB").toFixed(0)
        : formatBytes(cloudboxData.storage_box_type.size, "TB").toFixed(0)}
       <FormattedMessage
        id={formatBytes(cloudboxData.storage_box_type.size, "TB") < 1 ? "app.gb" : "app.tb"}
       />
      </Typography>
     </Stack>
    </Grid>
    <Grid xs={4}>
     <Stack alignItems="center">
      <SemiCircularProgress
       value={Number(
        ((cloudboxData?.stats.size_data / cloudboxData?.storage_box_type.size) * 100).toFixed(0)
       )}
       maxValue={100}
       size={80}
       thickness={8}
       percent={true}
      />
      <Typography variant="extraSmall" mt={0.5} textTransform="uppercase" fontWeight="bold">
       <FormattedMessage id="app.data" />
      </Typography>
     </Stack>
    </Grid>
    <Grid xs={4}>
     <Stack alignItems="center">
      <SemiCircularProgress
       value={Number(
        ((cloudboxData?.stats.size_snapshots / cloudboxData?.storage_box_type.size) * 100).toFixed(
         0
        )
       )}
       maxValue={100}
       size={80}
       thickness={8}
       percent={true}
      />
      <Typography variant="extraSmall" mt={0.5} textTransform="uppercase" fontWeight="bold">
       <FormattedMessage id="app.snapshot" />
      </Typography>
     </Stack>
    </Grid>
   </Grid>
  </Paper>
 ) : (
  <></>
 );
};

export default CloudboxMonitoring;
